import React from 'react';
import { InputNumber } from 'tea-component';
import classNames from 'classnames';
import { IEventCountInputProps } from './type';
import './index.scss';

const EventCountInput: React.FC<IEventCountInputProps> = (props) => {
  const {
    value,
    onChange,
    min = 0,
    max,
    step = 1,
    placeholder = 'Value',
    className,
    disabled = false,
    onFocus,
    hideButton = true,
  } = props;

  const [isFocused, setIsFocused] = React.useState(false);
  const valueRef = React.useRef(value);
  const onChangeRef = React.useRef(onChange);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    valueRef.current = value;
  }, [value]);

  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const clearTimers = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handleOperation = React.useCallback((type: 'inc' | 'dec') => {
    if (disabled) return;
    const currentVal = Math.trunc(Number(valueRef.current || 0));
    let newVal = type === 'inc' ? currentVal + Math.trunc(step) : currentVal - Math.trunc(step);

    const safeMax = max ?? Number.MAX_SAFE_INTEGER;
    if (type === 'inc' && newVal > safeMax) {
      newVal = safeMax;
    }
    if (type === 'dec' && min !== undefined && newVal < min) {
      newVal = min;
    }
    onChangeRef.current(String(newVal));
  }, [disabled, step, max, min]);

  const handleMouseDown = (type: 'inc' | 'dec') => (e: React.MouseEvent) => {
    e.preventDefault();
    if (disabled) return;

    handleOperation(type);

    timerRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        handleOperation(type);
      }, 100);
    }, 400);
  };

  const handleMouseUpOrLeave = React.useCallback(() => {
    clearTimers();
  }, [clearTimers]);

  const handleChange = (val?: number | string) => {
    if (val === undefined || val === null || val === '') {
      onChange('');
      return;
    }

    const numVal = Number(val);
    if (!Number.isFinite(numVal)) {
      onChange('');
      return;
    }
    const newVal = Math.trunc(numVal);
    if (min !== undefined && newVal < min) {
      onChange(String(min));
      return;
    }
    onChange(String(newVal));
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return (
    <div className={classNames('dbd-event-count-input', className, {
      'dbd-event-count-input--disabled': disabled,
      'dbd-event-count-input--focused': isFocused
    })}>
      <InputNumber
        allowEmpty
        value={value === '' ? undefined : Number(value)}
        onChange={handleChange}
        onInputChange={handleChange}
        min={min}
        step={Math.trunc(step)}
        precision={0}
        className="dbd-event-count-input__input-display"
        disabled={disabled}
        hideButton={hideButton}
        inputProps={{
          placeholder,
          onFocus: (e) => {
            setIsFocused(true);
            onFocus?.(e);
          },
          onBlur: handleBlur,
          onKeyDown: (e) => {
            const isAllowedSpecialKey = [
              'Backspace',
              'Delete',
              'Tab',
              'Escape',
              'Enter',
              'Home',
              'End',
              'ArrowLeft',
              'ArrowRight',
            ].includes(e.key);
            const isModifierPressed = e.ctrlKey || e.metaKey;
            const isClipboardAction = ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase());
            const isDigit = /[0-9]/.test(e.key);

            if (!isAllowedSpecialKey && !(isModifierPressed && isClipboardAction) && !isDigit) {
              e.preventDefault();
            }
          },
          style: { textAlign: 'left' },
        }}
      />
      <div className="dbd-event-count-input__arrows">
        <button
          type="button"
          className="dbd-event-count-input__arrow dbd-event-count-input__arrow--up"
          onMouseDown={handleMouseDown('inc')}
          onMouseUp={handleMouseUpOrLeave}
          onMouseLeave={handleMouseUpOrLeave}
          disabled={disabled}
          tabIndex={-1}
        >
          <i className="tea-icon tea-icon-arrowdown" />
        </button>
        <button
          type="button"
          className="dbd-event-count-input__arrow dbd-event-count-input__arrow--down"
          onMouseDown={handleMouseDown('dec')}
          onMouseUp={handleMouseUpOrLeave}
          onMouseLeave={handleMouseUpOrLeave}
          disabled={disabled}
          tabIndex={-1}
        >
          <i className="tea-icon tea-icon-arrowdown" />
        </button>
      </div>
    </div>
  );
};

export default EventCountInput;
